import React, { useState } from 'react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { 
  useGetPermissionsQuery, 
  useGetPermissionModulesQuery,
  useAddPermissionMutation,
  useUpdatePermissionMutation,
  useDeletePermissionMutation
} from '../../store/api';
import Pagination from '../Pagination';
import PermissionEditModal from './PermissionEditModal';
import { toast } from 'react-toastify';

export default function PermissionManagementModal({ isOpen, setIsModalOpen, roles = [] }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);

  const itemsPerPage = 10;

  // API hooks
  const { data: permissionsData, isLoading: permissionsLoading } = useGetPermissionsQuery();
  const { data: modulesData, isLoading: modulesLoading } = useGetPermissionModulesQuery();
  const [addPermission] = useAddPermissionMutation();
  const [updatePermission] = useUpdatePermissionMutation();
  const [deletePermission] = useDeletePermissionMutation();

  // Handle different API response formats
  const permissions = Array.isArray(permissionsData) ? permissionsData : (permissionsData?.data || permissionsData?.permissions || []);
  const apiModules = Array.isArray(modulesData) ? modulesData : (modulesData?.data || modulesData?.modules || []);

  // Extract unique modules from existing permissions
  const permissionModules = [...new Set(permissions.map(p => p.module).filter(Boolean))];
  
  // Combine API modules with permission modules and remove duplicates
  const allModules = [...new Set([...apiModules, ...permissionModules])].filter(Boolean).sort();

  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    module: ''
  });

  // Filter permissions
  const filteredPermissions = permissions.filter(permission => {
    const matchesSearch = 
      permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (permission.display_name && permission.display_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (permission.description && permission.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredPermissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPermissions = filteredPermissions.slice(startIndex, startIndex + itemsPerPage);

  // Permission CRUD handlers
  const handleAdd = () => {
    setEditingPermission(null);
    setFormData({
      name: '',
      display_name: '',
      description: '',
      module: ''
    });
    setIsEditModalOpen(true);
  };

  const handleEdit = (permission) => {
    setEditingPermission(permission);
    setFormData({
      name: permission.name || '',
      display_name: permission.display_name || permission.displayName || '',
      description: permission.description || '',
      module: permission.module || ''
    });
    setIsEditModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      toast.error('İcazə adı məcburidir!');
      return;
    }
    
    if (!formData.display_name.trim()) {
      toast.error('Görüntü adı məcburidir!');
      return;
    }

    if (!formData.module.trim()) {
      toast.error('Modul seçilməlidir!');
      return;
    }
    
    // Create permission object
    const permissionData = {
      name: formData.name.trim(),
      display_name: formData.display_name.trim(),
      description: formData.description.trim(),
      module: formData.module
    };
    
    try {
      if (editingPermission) {
        await updatePermission({ id: editingPermission.id, obj: permissionData }).unwrap();
        toast.success(`İcazə uğurla yeniləndi! ${permissionData.display_name}`);
      } else {
        await addPermission(permissionData).unwrap();
        toast.success(`İcazə uğurla əlavə edildi! ${permissionData.display_name}`);
      }
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Permission save error:', error);
      toast.error('Xəta baş verdi! Zəhmət olmasa yenidən cəhd edin.');
    }
  };

  const handleDelete = async (permission) => {
    // Check if permission is used in any role
    const rolesUsingPermission = roles.filter(role => {
      const rolePermissionIds = Array.isArray(role.permissions) 
        ? role.permissions.map(p => typeof p === 'object' ? p.id : p)
        : [];
      return rolePermissionIds.includes(permission.id);
    });
    
    if (rolesUsingPermission.length > 0) {
      toast.warning(`Bu icazə ${rolesUsingPermission.length} rol tərəfindən istifadə olunur və silinə bilməz!`);
      return;
    }
    
    if (window.confirm(`"${permission.display_name || permission.name}" icazəsini silmək istədiyinizə əminsiniz?\n\nBu əməliyyat geri alına bilməz.`)) {
      try {
        await deletePermission(permission.id).unwrap();
        toast.success(`"${permission.display_name || permission.name}" icazəsi uğurla silindi!`);
      } catch (error) {
        console.error('Permission delete error:', error);
        toast.error('Xəta baş verdi! Zəhmət olmasa yenidən cəhd edin.');
      }
    }
  };

  if (!isOpen) return null;

  if (permissionsLoading || modulesLoading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-5 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">İcazə İdarəetməsi</h3>
              <p className="mt-1 text-sm text-gray-600">
                Sistem icazələrini idarə edin
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleAdd}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Yeni İcazə
              </button>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="İcazə adı, təsvir..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Təmizlə
              </button>
            </div>
          </div>

          {/* Permissions table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İcazə
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Görüntü Adı
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modul
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Təsvir
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Əməliyyatlar
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedPermissions.map((permission) => (
                    <tr key={permission.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                            <ShieldCheckIcon className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{permission.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{permission.display_name || permission.displayName || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {permission.module || 'Yox'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">{permission.description || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(permission)}
                            className="inline-flex items-center p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-lg transition-colors"
                            title="Redaktə et"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(permission)}
                            className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg transition-colors"
                            title="Sil"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredPermissions.length === 0 && (
              <div className="text-center py-12">
                <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Heç bir icazə tapılmadı</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Axtarış şərtlərinizi dəyişin və ya yeni icazə əlavə edin.
                </p>
                <div className="mt-6">
                  <button
                    onClick={handleAdd}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                    İlk İcazəni Əlavə Et
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredPermissions.length > 0 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={filteredPermissions.length}
                itemsPerPage={itemsPerPage}
              />
            </div>
          )}

          {/* Modal Footer */}
          <div className="flex justify-end pt-6 border-t mt-6">
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Bağla
            </button>
          </div>
        </div>
      </div>

      {/* Permission Edit Modal */}
      <PermissionEditModal
        isOpen={isEditModalOpen}
        setIsModalOpen={setIsEditModalOpen}
        editingPermission={editingPermission}
        handleSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        modules={allModules}
      />
    </div>
  );
} 